import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, X, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { api, resolveImage, formatError } from "@/lib/api";

export async function uploadImages(files) {
  const urls = [];
  for (const file of files) {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    urls.push(data.url);
  }
  return urls;
}

const move = (arr, from, to) => {
  const n = [...arr];
  const [x] = n.splice(from, 1);
  n.splice(to, 0, x);
  return n;
};

export const ImageManager = ({ images, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);

  const handle = async (files) => {
    const list = files.filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setUploading(true);
    try {
      const urls = await uploadImages(list);
      onChange([...images, ...urls]);
      toast.success(`${urls.length} foto caricata/e`);
    } catch (e) {
      toast.error("Upload fallito", { description: formatError(e.response?.data?.detail) || e.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      data-testid="image-manager"
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handle(Array.from(e.dataTransfer.files)); }}
      className={`rounded-2xl p-2 transition-colors ${drag ? "bg-blue-50 ring-2 ring-brand-blue" : ""}`}
    >
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {images.map((im, i) => (
          <div key={`${im}-${i}`} data-testid={`img-tile-${i}`} className={`relative aspect-square rounded-xl overflow-hidden border-2 ${i === 0 ? "border-brand-yellow" : "border-border"}`}>
            <img src={resolveImage(im)} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="absolute top-1 left-1 bg-brand-yellow text-brand-blue text-[9px] font-black uppercase px-1.5 py-0.5 rounded">Principale</span>}
            <button type="button" data-testid={`remove-img-${i}`} onClick={() => onChange(images.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center">
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="absolute bottom-0 inset-x-0 flex justify-between bg-black/50 p-1">
              <button type="button" data-testid={`img-left-${i}`} disabled={i === 0} onClick={() => onChange(move(images, i, i - 1))} className="text-white disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
              {i !== 0 && (
                <button type="button" data-testid={`img-main-${i}`} onClick={() => onChange(move(images, i, 0))} className="text-brand-yellow" title="Imposta come principale"><Star className="w-4 h-4" /></button>
              )}
              <button type="button" data-testid={`img-right-${i}`} disabled={i === images.length - 1} onClick={() => onChange(move(images, i, i + 1))} className="text-white disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        <label data-testid="upload-btn" className="aspect-square rounded-xl border-2 border-dashed border-brand-blue/40 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50 transition-colors text-center px-1">
          <Upload className="w-6 h-6 text-brand-blue" strokeWidth={2.5} />
          <span className="text-[10px] mt-1 text-brand-blue font-bold uppercase">{uploading ? "Caricamento..." : "Carica o trascina"}</span>
          <input data-testid="upload-input" type="file" accept="image/*" multiple className="hidden" disabled={uploading} onChange={(e) => { handle(Array.from(e.target.files)); e.target.value = ""; }} />
        </label>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">La prima foto è quella principale. Usa la stella per cambiarla e le frecce per riordinare.</p>
    </div>
  );
};
